import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { AppErrors, ConflictError, NotFoundError } from '@fitness-force/models';
import { environment } from '@fitness-force/environments';
import { PaymentGatewaySupported } from '@fitness-force/enum';
import type { TENANT_MODEL } from '@fitness-force/models';
import { TimeconversionService } from '../common/timeconversion.service';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private timeService = inject(TimeconversionService);

  private isTenantPresale = new BehaviorSubject<boolean>(false);
  private tenantStartDate: Date | null = null;
  isPresaleTenant$ = this.isTenantPresale.asObservable();

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  GetTenantList(): Observable<any> {
    return this.http
      .get(environment.BASE_URL + CONST_URL_SEGEMENTS.TENANT.GET_TENANT_LIST, {
        headers: this.httpOptions.headers,
      })
      .pipe(
        map((res: any) => {
          if (res) {
            return res;
          } else {
            return null;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  GetTenantById(BASE_URL: string, tenantId: number): Observable<any> {
    return this.http
      .get(
        BASE_URL + CONST_URL_SEGEMENTS.TENANT.GET_TENANTBYID + '/' + tenantId,
        { headers: this.httpOptions.headers }
      )
      .pipe(
        map((res: any) => {
          if (res) {
            return res;
          } else {
            return null;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  CreateTenant(createTenantPayload: any): Observable<any> {
    return this.http
      .post(
        environment.BASE_URL + CONST_URL_SEGEMENTS.TENANT.CREATE_TENANT,
        createTenantPayload,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          if (error.status === 409) {
            return throwError(() => new ConflictError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  GetTenantPosDevicesById(
    tenantId: number,
    paymentGateway: PaymentGatewaySupported
  ): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.TENANT.GET_TENANT_POS_DEVICES
      .replace('{tenant_id}', tenantId.toString())
      .replace('{payment_gateway}', paymentGateway.toString());
    return this.http.get(url, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        if (res) {
          return res;
        } else {
          return null;
        }
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  ActiveTenant(tenantId: number) {
    return this.http
      .post(
        environment.BASE_URL + CONST_URL_SEGEMENTS.TENANT.MARK_TENANT_ACTIVE.replace('{tenant_id}', tenantId.toString()),
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  InActiveTenant(tenantId: number) {
    return this.http
      .post(
        environment.BASE_URL + CONST_URL_SEGEMENTS.TENANT.MARK_TENANT_INACTIVE.replace('{tenant_id}', tenantId.toString()),
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  patchTenant(tenantId: number, data: any) {
    return this.http
      .patch(
        environment.BASE_URL + CONST_URL_SEGEMENTS.TENANT.PATCH_TENANT
          .replace('{tenant_id}', tenantId.toString()),
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateTenantPresaleStatus(res: TENANT_MODEL): void {
    if (res) {
      this.isTenantPresale.next(res.status.toLowerCase() === 'presales');
      if (this.isTenantPresale.value) {
        this.tenantStartDate = this.timeService.ConvertUTCToLocalDateTime(
          res.tenant_start_date
        );
      }
    }
  }

  getFranchiseeList(): Observable<any> {
    return this.http.get(environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUPS.GET_FRANCHISE_GROUPS, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getContactList(params: any): Observable<any> {
    let parsedParams = new HttpParams();
    if (params != null) {
      params.forEach((p: any) => {
        parsedParams = parsedParams.append(p.name, p.value);
      });
    }
    return this.http.get(`${environment.BASE_URL}${CONST_URL_SEGEMENTS.TENANT.GET_CONTACTS}`, { headers: this.httpOptions.headers, params: parsedParams }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getTenantStartDate(): Date | null {
    return this.tenantStartDate;
  }

  IsPresaleTenant(): boolean {
    return this.isTenantPresale.value;
  }
}
